import dayjs from "dayjs";
import { FiArrowUpRight, FiMapPin, FiShare2, FiEdit2, FiTrash2, FiAlertTriangle } from "react-icons/fi";
import {useState} from "react";
import CreateEventForm from "../forms/CreateEventForm.jsx";
import {useDeleteEvent} from "./useDeleteEvent.js";
import {useUser} from "../hooks/useUser.js";
import Button from "../ui/Button.jsx";
import { safeExternalUrl } from "./eventValidation.js";

import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

function EventBox({ event }) {
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const { isDeleting, deleteEvent } = useDeleteEvent();
    const { user, isAuthenticated } = useUser();
    const isAdmin = isAuthenticated && user?.role === "admin";
    const ticketsUrl = safeExternalUrl(event.ticketsUrl);
    const venue = event.venue?.trim();
    const hasKnownVenue = venue && !/\b(tba|tbc|tbd|undisclosed|secret|to be announced|to be confirmed|to be determined|location pending)\b|^unknown$|^n\/?a$/i.test(venue);
    const mapQuery = [venue, event.city, event.state, event.country]
        .filter(value => typeof value === "string" && value.trim())
        .map(value => value.trim())
        .join(", ");
    const mapsUrl = hasKnownVenue
        ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapQuery)}`
        : "";
    // Temporary artwork override for this specific event until image uploads exist.
    const eventImage = String(event.id) === "112"
        ? "/images/sponsors/Mixr/MIXR.MELB%20Locktober%20Launch%20Party.png"
        : null;

    return (
        <div
            className={`event-card ${event.is_sponsored === true ? "event-sponsored" : ""}`}
        >
            {event.is_sponsored === true && (
                <span className="event-sponsored-badge"><span aria-hidden="true">✦ </span>Community Partner</span>
            )}
            <div className="event-card-heading">
                <div className="event-date" aria-hidden="true">
                    <span>{dayjs.utc(event.startDatetime).local().format("MMM")}</span>
                    <strong>{dayjs.utc(event.startDatetime).local().format("DD")}</strong>
                    <span>{dayjs.utc(event.startDatetime).local().format("ddd")}</span>
                </div>
                <div className="event-heading-copy">
                    <time className="event-time" dateTime={event.startDatetime}>
                        {dayjs.utc(event.startDatetime).local().format("ddd, D MMM YYYY · HH:mm")}
                    </time>
                    <h2 className="event-title">{event.name}</h2>
                    {(event.venue || event.city) && (
                        <p className="event-location"><FiMapPin aria-hidden="true" />
                            <span>{[event.venue, event.city].filter(Boolean).join(" · ")}</span>
                        </p>
                    )}
                </div>
            </div>
                <div className="event-card-body">
                    {eventImage && (
                        <img
                            src={eventImage}
                            alt="MIXR.MELB Locktober Launch Party promotional flyer"
                            width={747}
                            height={377}
                            className="w-full h-auto rounded-xl mb-4"
                            loading="lazy"
                        />
                    )}
                    {event.description && (
                        <p className="event-description">{event.description}</p>
                    )}
                    
                    <div className="event-actions">
                        {ticketsUrl && (
                            <Button className="event-action event-action-primary" size="small" href={ticketsUrl} target="_blank" rel="noopener noreferrer">
                                Tickets <FiArrowUpRight aria-hidden="true" />
                            </Button>
                        )}
                        {mapsUrl && (
                            <Button className="event-action" size="small" href={mapsUrl} target="_blank" rel="noopener noreferrer"
                                    aria-label={`View ${venue} on Google Maps (opens in a new tab)`}>
                                <FiMapPin aria-hidden="true" /> Map
                            </Button>
                        )}
                        <Button className="event-action" size="small"
                                onClick={(e) => {
                                    e.stopPropagation(); // Prevent box toggle
                                    const shareUrl = window.location.origin + `/events/${encodeURIComponent(event.id)}`;
                                    const shareText = `Check out this party: ${event.name} on ${dayjs.utc(event.startDatetime).local().format("DD MMM YYYY - HH:mm")} in ${event.city}, ${event.state}`;
                                    if (navigator.share) {
                                        navigator.share({ title: event.name, text: shareText, url: shareUrl }).catch(console.error);
                                    } else {
                                        navigator.clipboard.writeText(shareUrl)
                                            .then(() => alert("Link copied to clipboard!"))
                                            .catch(() => alert("Could not copy the link"));
                                    }
                                }}>
                            <FiShare2 aria-hidden="true" /> Share
                        </Button>

                    {isAdmin && (
                        <>
                            <Button className="event-admin-action" size="small" aria-expanded={showForm} onClick={(e) => { e.stopPropagation(); setShowForm((s) => !s); }}>
                                <FiEdit2 aria-hidden="true" /> Edit event
                            </Button>
                            <Button className="event-admin-action" size="small" aria-expanded={showConfirmDelete} onClick={(e) => { e.stopPropagation(); setShowConfirmDelete((s) => !s); }} disabled={isDeleting}>
                                <FiTrash2 aria-hidden="true" /> Delete event <span className="event-admin-id">#{event.id}</span>
                            </Button>
                        </>
                    )}
                    </div>

                    {isAdmin && showConfirmDelete && (
                        <div className="event-delete-warning">
                            <div className="event-delete-message" role="alert">
                                <FiAlertTriangle aria-hidden="true" />
                                <div>
                                    <h3>Delete this event permanently?</h3>
                                    <p>This will remove “{event.name}”. This action cannot be undone.</p>
                                </div>
                            </div>
                            <div className="event-delete-actions">
                                <Button type="button" size="small" className="event-delete-confirm" onClick={() => deleteEvent(event.id)} disabled={isDeleting}>
                                    <FiTrash2 aria-hidden="true" /> {isDeleting ? "Deleting…" : "Confirm deletion"}
                                </Button>
                                <Button type="button" size="small" className="event-delete-cancel" onClick={() => setShowConfirmDelete(false)} disabled={isDeleting}>
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    )}

                    {isAdmin && showForm && <CreateEventForm eventToEdit={event} onCloseModal={() => setShowForm(false)} />}
                </div>


        </div>
    );
}


export default EventBox;
